import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useBulkUpdateWorkflows } from '@frontend/api';
import {
  Button,
  Divider,
  Modal,
  ModalBody,
  ModalButtonGroup,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  VStack,
} from '@frontend/legacy-components';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';
import { broadcastQuoteItemWorkflowChange } from 'modules/quotes/quoteDetailsPage/hooks/useSyncQpvAndBaqTabs';
import { invalidateAllAssemblyTreeQueries } from 'modules/quotes/quoteItemComponents/assemblyTree/data/utils';
import { getQuoteWorkflowSteps } from 'modules/quotes/quoteItems/data/selectors';

import { getQuoteId } from 'store/quotes/selectors';
import { useFeatureFlagValue } from 'store/session/hooks';

import { notifyError, notifySuccess } from 'utils/notifyUtils';

import { quoteItemSelect } from '../data/actions';
import WorkflowStatusSelect from './components/WorkflowStatusSelect';
import WorkflowStepSelectsGroup from './components/WorkflowStepSelectsGroup';
import { close, save } from './data/actions';
import { getComponentIds, getQuoteItemIds, getIsOpen } from './data/reducer';
import { QuoteWorkflowStep, SelectorOption } from './data/types';

export const BulkUpdateWorkflowStatusModal = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector(getIsOpen);
  const quoteId = useSelector(getQuoteId);
  // NOTE: quote item ids will be undefined when bulk selecting child components
  const quoteItemIds = useSelector(getQuoteItemIds);
  // NOTE: component ids will be undefined when bulk selecting root components
  const componentIds = useSelector(getComponentIds);
  const hasCustomWorkflows = useFeatureFlagValue(
    featureFlagKeys.isWorkflowsAvailable,
  );

  const quoteWorkflowSteps: QuoteWorkflowStep[] = useSelector(
    getQuoteWorkflowSteps,
  );
  const memoizedSortedWorkflowSteps: QuoteWorkflowStep[] = useMemo(
    () => quoteWorkflowSteps.sort((a, b) => a.position - b.position),
    [quoteWorkflowSteps],
  );

  const [quoteItemStatusSelectorValue, setQuoteItemStatusSelectorValue] =
    useState<SelectorOption>();
  const [
    customWorkflowStepIdToStatusDictionary,
    setCustomWorkflowStepIdToStatusDictionary,
  ] = useState<Record<string, SelectorOption>>({});

  const resetState = () => {
    setQuoteItemStatusSelectorValue(undefined);
    setCustomWorkflowStepIdToStatusDictionary({});
  };

  const onClose = () => {
    dispatch(close());
    resetState();
  };

  const { mutate: bulkUpdateWorkflows, isLoading } = useBulkUpdateWorkflows({
    queryOptions: {
      onSuccess: (data) => {
        dispatch(quoteItemSelect.deselectAll());
        invalidateAllAssemblyTreeQueries();
        notifySuccess('Updated workflow status');
        dispatch(save({ data }));
        // if quote item ids specified, this means we need to broadcast the change to the QPV
        if (quoteItemIds) {
          for (const quoteItemId of quoteItemIds) {
            broadcastQuoteItemWorkflowChange({
              context: {
                quoteItemId,
                isRootComponent: true,
              },
            });
          }
        }
        onClose();
      },
      onError: () => {
        notifyError("Couldn't update workflow status");
        onClose();
      },
    },
  });

  const onSubmit = useCallback(
    () =>
      bulkUpdateWorkflows({
        quoteId,
        quoteItems: quoteItemIds,
        components: componentIds,
        workflowStatus: quoteItemStatusSelectorValue?.value,
        customWorkflowSteps: Object.keys(
          customWorkflowStepIdToStatusDictionary,
        ).map((id) => ({
          id: id,
          status: customWorkflowStepIdToStatusDictionary[id].value,
        })),
        applyToChildComponents: false,
      }),
    [
      bulkUpdateWorkflows,
      quoteId,
      quoteItemIds,
      componentIds,
      quoteItemStatusSelectorValue,
      customWorkflowStepIdToStatusDictionary,
    ],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <ModalContent>
        <ModalHeader>
          Edit Workflow Status
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody>
          <VStack spacing={1}>
            <WorkflowStatusSelect
              onChange={setQuoteItemStatusSelectorValue}
              value={quoteItemStatusSelectorValue}
            />
            {hasCustomWorkflows &&
            memoizedSortedWorkflowSteps.length &&
            !componentIds?.length ? (
              <>
                <Divider pt={1.5} />
                <WorkflowStepSelectsGroup
                  setValuesDict={setCustomWorkflowStepIdToStatusDictionary}
                  valuesDict={customWorkflowStepIdToStatusDictionary}
                  workflowSteps={memoizedSortedWorkflowSteps}
                />
              </>
            ) : null}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <ModalButtonGroup>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={onSubmit}
              isLoading={isLoading}
              isDisabled={
                !quoteItemStatusSelectorValue &&
                hasCustomWorkflows &&
                !Object.values(customWorkflowStepIdToStatusDictionary).length
              }
            >
              Update Workflow Status
            </Button>
          </ModalButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BulkUpdateWorkflowStatusModal;
